// src/services/tenantService.js
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase-config";
import slugify from "slugify";

// Restaurant/Tenant Management Service
export class TenantService {
  constructor() {
    this.restaurantsCollection = "restaurants";
    this.defaultTheme = {
      primaryColor: "#d4af37",
      secondaryColor: "#31231f",
      backgroundColor: "#f8f4e3",
      textColor: "#31231f",
      fontFamily: "Arial, sans-serif",
      titleFont: "Georgia, serif",
      logoUrl: null,
      customCss: "",
    };
  }

  // Create a new restaurant/tenant
  async createRestaurant(restaurantData, ownerEmail, ownerPassword) {
    try {
      // Generate unique slug for the restaurant
      const slug = slugify(restaurantData.name, {
        lower: true,
        strict: true,
      });

      // Check if slug already exists
      const existingRestaurant = await this.getRestaurantBySlug(slug);
      if (existingRestaurant) {
        throw new Error(
          "Restaurant name already exists. Please choose a different name."
        );
      }

      // Create the restaurant document
      const restaurant = {
        name: restaurantData.name,
        slug: slug,
        description: restaurantData.description || "",
        address: restaurantData.address || "",
        phone: restaurantData.phone || "",
        email: restaurantData.email || "",
        website: restaurantData.website || "",
        theme: { ...this.defaultTheme, ...restaurantData.theme },
        settings: {
          allowOnlineOrdering: false,
          showPrices: true,
          showDescriptions: true,
          enableWineComparison: true,
          timezone: "America/New_York",
          currency: "USD",
          ...restaurantData.settings,
        },
        subscription: {
          plan: "free",
          status: "active",
          trialEnds: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days trial
          features: {
            maxMenuItems: 100,
            customBranding: false,
            documentUpload: true,
            analytics: false,
          },
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        ownerId: null, // Will be set after user creation
      };

      // Add restaurant to Firestore
      const restaurantRef = await addDoc(
        collection(db, this.restaurantsCollection),
        restaurant
      );

      return {
        id: restaurantRef.id,
        ...restaurant,
      };
    } catch (error) {
      console.error("Error creating restaurant:", error);
      throw error;
    }
  }

  // Get restaurant by slug
  async getRestaurantBySlug(slug) {
    try {
      const q = query(
        collection(db, this.restaurantsCollection),
        where("slug", "==", slug)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      };
    } catch (error) {
      console.error("Error getting restaurant by slug:", error);
      throw error;
    }
  }

  // Get restaurant by ID
  async getRestaurantById(restaurantId) {
    try {
      const restaurantRef = doc(db, this.restaurantsCollection, restaurantId);
      const restaurantDoc = await getDoc(restaurantRef);

      if (!restaurantDoc.exists()) {
        return null;
      }

      return {
        id: restaurantDoc.id,
        ...restaurantDoc.data(),
      };
    } catch (error) {
      console.error("Error getting restaurant by ID:", error);
      throw error;
    }
  }

  // Update restaurant settings
  async updateRestaurant(restaurantId, updates) {
    try {
      const restaurantRef = doc(db, this.restaurantsCollection, restaurantId);
      await updateDoc(restaurantRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return true;
    } catch (error) {
      console.error("Error updating restaurant:", error);
      throw error;
    }
  }

  // Update restaurant theme
  async updateTheme(restaurantId, themeUpdates) {
    try {
      const restaurant = await this.getRestaurantById(restaurantId);
      if (!restaurant) {
        throw new Error("Restaurant not found");
      }

      const updatedTheme = {
        ...restaurant.theme,
        ...themeUpdates,
      };

      await this.updateRestaurant(restaurantId, { theme: updatedTheme });
      return updatedTheme;
    } catch (error) {
      console.error("Error updating restaurant theme:", error);
      throw error;
    }
  }

  // Get all restaurants (admin function)
  async getAllRestaurants() {
    try {
      const q = query(
        collection(db, this.restaurantsCollection),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting all restaurants:", error);
      throw error;
    }
  }

  // Generate CSS variables from theme
  generateThemeCSS(theme) {
    return `
      :root {
        --primary-color: ${theme.primaryColor};
        --secondary-color: ${theme.secondaryColor};
        --background-color: ${theme.backgroundColor};
        --text-color: ${theme.textColor};
        --font-family: ${theme.fontFamily};
        --title-font: ${theme.titleFont};
      }
      
      ${theme.customCss || ""}
    `;
  }

  // Apply theme to the page
  applyTheme(theme) {
    // Remove existing theme styles
    const existingThemeStyle = document.getElementById("restaurant-theme");
    if (existingThemeStyle) {
      existingThemeStyle.remove();
    }

    // Create and apply new theme styles
    const style = document.createElement("style");
    style.id = "restaurant-theme";
    style.textContent = this.generateThemeCSS(theme);
    document.head.appendChild(style);
  }

  /**
   * PHASE 2 INTEGRATION METHODS
   * Enhanced methods for AI-powered tenant management
   */

  /**
   * Create tenant with AI-enhanced setup
   */
  async createTenant(restaurantData, options = {}) {
    try {
      // Use createRestaurant as base but with enhanced features
      const baseRestaurant = await this.createRestaurant(
        restaurantData,
        options.ownerEmail,
        options.ownerPassword
      );

      // Add Phase 2 enhancements
      const enhancedRestaurant = await this.enhanceRestaurantWithAI(
        baseRestaurant.id,
        options
      );

      return {
        ...baseRestaurant,
        aiEnhanced: enhancedRestaurant,
        phase2Features: true,
      };
    } catch (error) {
      console.error("Error creating AI-enhanced tenant:", error);
      throw error;
    }
  }

  /**
   * Enhance restaurant with AI capabilities
   */
  async enhanceRestaurantWithAI(restaurantId, options = {}) {
    try {
      // Import AI services dynamically
      const { masterOrchestrator } = await import(
        "./masterServiceOrchestrator.js"
      );
      await masterOrchestrator.initialize();

      // Get restaurant data
      const restaurant = await this.getRestaurant(restaurantId);
      if (!restaurant) {
        throw new Error("Restaurant not found");
      }

      // AI enhancements
      const enhancements = {
        aiFeatures: {
          intelligentPairing: true,
          menuOptimization: true,
          beverageRecommendations: true,
          conversationalAI: true,
          foodMenuParsing: true,
        },
        preferences: {
          enhancementLevel: options.enhancementLevel || "standard",
          autoEnhance: options.autoEnhance !== false,
          externalApiIntegration: options.externalApiIntegration !== false,
          analyticsEnabled: options.analyticsEnabled !== false,
        },
        configuration: {
          aiModels: {
            primary: "claude-3-5-sonnet",
            fallback: "gemini-pro",
          },
          enhancementSettings: {
            batchSize: 10,
            delayBetweenRequests: 1000,
            maxRetries: 3,
          },
        },
        setupTimestamp: new Date().toISOString(),
        version: "2.0",
      };

      // Update restaurant document with AI enhancements
      await this.updateRestaurant(restaurantId, {
        aiEnhancements: enhancements,
        updatedAt: new Date().toISOString(),
      });

      return enhancements;
    } catch (error) {
      console.error("Error enhancing restaurant with AI:", error);
      throw error;
    }
  }

  /**
   * Get tenant analytics and insights
   */
  async getTenantAnalytics(restaurantId, timeframe = "30d") {
    try {
      // Import analytics service
      const { getEnhancementMetrics } = await import(
        "./enhancementAnalyticsService.js"
      );

      const analytics = {
        overview: {
          restaurantId,
          timeframe,
          generatedAt: new Date().toISOString(),
        },
        enhancement: await getEnhancementMetrics(restaurantId, timeframe),
        menu: await this.getMenuAnalytics(restaurantId),
        performance: await this.getPerformanceMetrics(restaurantId, timeframe),
        recommendations: await this.getAnalyticsRecommendations(restaurantId),
      };

      return analytics;
    } catch (error) {
      console.error("Error getting tenant analytics:", error);
      return {
        error: error.message,
        restaurantId,
        timeframe,
      };
    }
  }

  /**
   * Get menu analytics for tenant
   */
  async getMenuAnalytics(restaurantId) {
    try {
      const { menuService } = await import("./menuService.js");
      const stats = await menuService.getMenuStatistics(restaurantId);

      return {
        totalItems: stats.total,
        enhancedItems: stats.enhanced,
        enhancementRate:
          stats.total > 0 ? (stats.enhanced / stats.total) * 100 : 0,
        categoryBreakdown: stats.byCategory,
        averagePrice: stats.averagePrice,
        priceRange: stats.priceRange,
      };
    } catch (error) {
      console.error("Error getting menu analytics:", error);
      return { error: error.message };
    }
  }

  /**
   * Get performance metrics for tenant
   */
  async getPerformanceMetrics(restaurantId, timeframe) {
    // This would integrate with POS/analytics systems in production
    return {
      placeholder: true,
      message: "Performance metrics integration pending",
      metrics: {
        totalOrders: 0,
        revenue: 0,
        averageOrderValue: 0,
        popularItems: [],
        conversionRate: 0,
      },
    };
  }

  /**
   * Generate analytics recommendations for tenant
   */
  async getAnalyticsRecommendations(restaurantId) {
    try {
      const recommendations = [];

      // Get menu analytics
      const menuAnalytics = await this.getMenuAnalytics(restaurantId);

      // Enhancement recommendations
      if (menuAnalytics.enhancementRate < 50) {
        recommendations.push({
          type: "enhancement",
          priority: "high",
          title: "Improve Menu Enhancement Rate",
          description: `Only ${Math.round(
            menuAnalytics.enhancementRate
          )}% of your menu items are AI-enhanced`,
          action: "enhance_remaining_items",
        });
      }

      // Category diversity recommendations
      if (Object.keys(menuAnalytics.categoryBreakdown).length < 3) {
        recommendations.push({
          type: "menu_diversity",
          priority: "medium",
          title: "Diversify Menu Categories",
          description:
            "Consider adding items from different beverage categories",
          action: "add_categories",
        });
      }

      // Pricing recommendations
      if (menuAnalytics.priceRange.max - menuAnalytics.priceRange.min > 100) {
        recommendations.push({
          type: "pricing",
          priority: "low",
          title: "Consider Price Point Balance",
          description: "Large price range detected - consider filling gaps",
          action: "optimize_pricing",
        });
      }

      return recommendations;
    } catch (error) {
      console.error("Error generating analytics recommendations:", error);
      return [];
    }
  }

  /**
   * Update tenant AI preferences
   */
  async updateAIPreferences(restaurantId, preferences) {
    try {
      const restaurant = await this.getRestaurant(restaurantId);
      if (!restaurant) {
        throw new Error("Restaurant not found");
      }

      const currentPreferences = restaurant.aiEnhancements?.preferences || {};
      const updatedPreferences = {
        ...currentPreferences,
        ...preferences,
        updatedAt: new Date().toISOString(),
      };

      await this.updateRestaurant(restaurantId, {
        "aiEnhancements.preferences": updatedPreferences,
      });

      return updatedPreferences;
    } catch (error) {
      console.error("Error updating AI preferences:", error);
      throw error;
    }
  }

  /**
   * Get tenant AI configuration
   */
  async getAIConfiguration(restaurantId) {
    try {
      const restaurant = await this.getRestaurant(restaurantId);
      if (!restaurant) {
        throw new Error("Restaurant not found");
      }

      return (
        restaurant.aiEnhancements || {
          aiFeatures: { intelligentPairing: false },
          preferences: { enhancementLevel: "basic" },
          configuration: { aiModels: { primary: "claude-3-5-sonnet" } },
        }
      );
    } catch (error) {
      console.error("Error getting AI configuration:", error);
      throw error;
    }
  }

  /**
   * Migrate tenant to Phase 2
   */
  async migrateTenantToPhase2(restaurantId, options = {}) {
    try {
      // Check if already migrated
      const restaurant = await this.getRestaurant(restaurantId);
      if (restaurant?.aiEnhancements?.version === "2.0") {
        return {
          alreadyMigrated: true,
          version: "2.0",
          message: "Tenant already migrated to Phase 2",
        };
      }

      // Perform migration
      const migration = {
        started: new Date().toISOString(),
        fromVersion: restaurant?.aiEnhancements?.version || "1.0",
        toVersion: "2.0",
        steps: [],
      };

      // Step 1: Enhance with AI features
      migration.steps.push({
        step: "ai_enhancement",
        status: "started",
        timestamp: new Date().toISOString(),
      });
      await this.enhanceRestaurantWithAI(restaurantId, options);
      migration.steps.push({
        step: "ai_enhancement",
        status: "completed",
        timestamp: new Date().toISOString(),
      });

      // Step 2: Update schema if needed
      migration.steps.push({
        step: "schema_update",
        status: "started",
        timestamp: new Date().toISOString(),
      });
      const { firestoreSchemaService } = await import(
        "./firestoreSchemaService.js"
      );
      await firestoreSchemaService.migrateRestaurantData(restaurantId);
      migration.steps.push({
        step: "schema_update",
        status: "completed",
        timestamp: new Date().toISOString(),
      });

      // Step 3: Initialize AI services
      migration.steps.push({
        step: "ai_initialization",
        status: "started",
        timestamp: new Date().toISOString(),
      });
      const { masterOrchestrator } = await import(
        "./masterServiceOrchestrator.js"
      );
      await masterOrchestrator.initialize();
      migration.steps.push({
        step: "ai_initialization",
        status: "completed",
        timestamp: new Date().toISOString(),
      });

      migration.completed = new Date().toISOString();
      migration.success = true;

      // Update restaurant with migration info
      await this.updateRestaurant(restaurantId, {
        "aiEnhancements.migration": migration,
        "aiEnhancements.version": "2.0",
      });

      return migration;
    } catch (error) {
      console.error("Error migrating tenant to Phase 2:", error);
      throw error;
    }
  }

  /**
   * Get tenant feature capabilities
   */
  getTenantCapabilities(restaurant) {
    const aiEnhancements = restaurant?.aiEnhancements;

    return {
      hasAIFeatures: !!aiEnhancements,
      version: aiEnhancements?.version || "1.0",
      features: {
        intelligentPairing:
          aiEnhancements?.aiFeatures?.intelligentPairing || false,
        menuOptimization: aiEnhancements?.aiFeatures?.menuOptimization || false,
        beverageRecommendations:
          aiEnhancements?.aiFeatures?.beverageRecommendations || false,
        conversationalAI: aiEnhancements?.aiFeatures?.conversationalAI || false,
        foodMenuParsing: aiEnhancements?.aiFeatures?.foodMenuParsing || false,
      },
      preferences: aiEnhancements?.preferences || {},
      setupComplete: !!aiEnhancements?.setupTimestamp,
    };
  }
}

// Create singleton instance
export const tenantService = new TenantService();
